import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import classes from './auth.module.css';
import Header from '../../components/standartComponents/header';
import { signIn, signUp } from '../../lib/allauth';
import { useTranslation } from 'react-i18next';

const EyeOpen = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
);
const EyeClosed = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" x2="23" y1="1" y2="23"/></svg>
);

const Auth = ({ authUser, initialMode = 'login' }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mode, setMode] = useState(initialMode); // 'login' | 'register' | 'forgot'

  // Sync mode when initialMode prop changes (e.g. navigating /signin ↔ /signup via header)
  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);
  
  // Form fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [terms, setTerms] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');

  // Errors
  const [errors, setErrors] = useState({});
  const [bannerType, setBannerType] = useState(null); // 'exists' | 'tech'

  // Show password toggle
  const [showPassword, setShowPassword] = useState(false);

  // Loading
  const [loading, setLoading] = useState(false);

  // Forgot password states
  const [forgotSent, setForgotSent] = useState(false);

  const showForm = (newMode) => {
    setMode(newMode);
    setErrors({});
    setBannerType(null);
    setLoading(false);
    if (newMode !== 'forgot') {
      setName('');
      setEmail('');
      setPassword('');
      setTerms(false);
    }
    if (newMode === 'forgot') {
      setForgotSent(false);
      setForgotEmail('');
    }
    if (newMode === 'login') navigate('/signin', { replace: true });
    if (newMode === 'register') navigate('/signup', { replace: true });
  };

  const validateRegister = async () => {
    setErrors({});
    setBannerType(null);
    const errs = {};
    if (!name.trim()) errs.name = true;
    if (!email || !email.includes('@') || !email.includes('.')) errs.email = true;
    if (password.length < 8) errs.password = true;
    if (!terms) errs.terms = true;

    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setLoading(true);
    try {
      const response = await signUp({ email, username: name, password });
      if (response && response.meta && response.meta.is_authenticated) {
        if (window.refreshAuthUser) await window.refreshAuthUser();
        navigate('/registrationSuccess');
      } else {
        setBannerType('tech');
      }
    } catch (err) {
      if (err.message && err.message.toLowerCase().includes('already exists')) {
        setBannerType('exists');
      } else {
        setBannerType('tech');
      }
    } finally {
      setLoading(false);
    }
  };

  const validateLogin = async () => {
    setErrors({});
    setBannerType(null);
    const errs = {};
    if (!email || !email.includes('@')) errs.email = true;
    if (!password) errs.password = true;

    setErrors(errs);
    if (Object.keys(errs).length === 0) {
      setLoading(true);
      try {
        const response = await signIn({ email, password });
        if (response && response.meta && response.meta.is_authenticated) {
          if (window.refreshAuthUser) await window.refreshAuthUser();
          navigate('/');
        } else {
          setErrors({ email: true, password: true });
          setBannerType('tech');
        }
      } catch (err) {
        setErrors({ email: true, password: true });
        setBannerType('tech');
      } finally {
        setLoading(false);
      }
    }
  };

  const submitForgot = () => {
    setErrors({});
    setBannerType(null);
    if (!forgotEmail || !forgotEmail.includes('@') || !forgotEmail.includes('.')) {
      setErrors({ forgotEmail: true });
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setForgotSent(true);
    }, 1200);
  };

  return (
    <>
      <Header authUser={authUser} />
      
      <div className={classes.main}>
        {/* FORM CARD */}
        <div className={classes.formCard}>

          {/* REGISTER FORM */}
          {mode === 'register' && (
            <div>
              <div className={classes.formTitle}>{t('Create Account')}</div>
              <div className={classes.formSub}>{t("Register to save your progress and get a personalized training plan.")}</div>

              {/* Error banner: email exists */}
              {bannerType === 'exists' && (
                <div className={`${classes.errBanner} ${classes.errBannerRose}`}>
                  <div className={classes.ebTop}>
                    <div className={`${classes.ebIcon} ${classes.ebIconRose}`}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>
                    </div>
                    <div className={classes.ebBody}>
                      <div className={classes.ebTitle}>Не вдалося створити акаунт</div>
                      <div className={classes.ebText}>Цей email уже зареєстрований. Увійдіть або використайте іншу адресу.</div>
                      <div className={classes.ebActions}>
                        <span onClick={() => showForm('login')}>{t('Log In')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Error banner: technical */}
              {bannerType === 'tech' && (
                <div className={`${classes.errBanner} ${classes.errBannerAmber}`}>
                  <div className={classes.ebTop}>
                    <div className={`${classes.ebIcon} ${classes.ebIconAmber}`}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
                    </div>
                    <div className={classes.ebBody}>
                      <div className={classes.ebTitle}>Щось пішло не так</div>
                      <div className={classes.ebText}>Не вдалося створити акаунт. Спробуйте ще раз за кілька хвилин.</div>
                      <div className={classes.ebActions}>
                        <span onClick={validateRegister}>Спробувати ще раз</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className={classes.fg}>
                <label className={classes.fl}>{t('Name')}</label>
                <input className={`${classes.fi} ${errors.name ? classes.err : ''}`} placeholder={t('Your name')} value={name} onChange={(e) => setName(e.target.value)} />
                <div className={`${classes.fiErr} ${errors.name ? classes.show : ''}`}>{t('Enter your name')}</div>
              </div>

              <div className={classes.fg}>
                <label className={classes.fl}>Email</label>
                <input className={`${classes.fi} ${errors.email ? classes.err : ''}`} type="email" placeholder="example@email.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                <div className={`${classes.fiErr} ${errors.email ? classes.show : ''}`}>{t('Enter a valid email address')}</div>
              </div>

              <div className={classes.fg}>
                <label className={classes.fl}>{t('Password')}</label>
                <div className={classes.pwWrap}>
                  <input className={`${classes.fi} ${errors.password ? classes.err : ''}`} type={showPassword ? 'text' : 'password'} placeholder={t("Your password")} value={password} onChange={(e) => setPassword(e.target.value)} />
                  <button className={classes.pwToggle} type="button" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeClosed /> : <EyeOpen />}
                  </button>
                </div>
                <div className={classes.fiHint}>{t('Minimum 8 characters.')}</div>
                <div className={`${classes.fiErr} ${errors.password ? classes.show : ''}`}>{t('Password must contain at least 8 characters')}</div>
              </div>

              <div className={classes.checkRow}>
                <input type="checkbox" id="rTerms" checked={terms} onChange={(e) => setTerms(e.target.checked)} />
                <label htmlFor="rTerms">{t('I agree to the')} <Link to="/termsOfUse">{t('Terms of Use')}</Link> {t('and')} <Link to="/privacy">{t('Privacy Policy')}</Link></label>
              </div>
              <div className={`${classes.fiErr} ${errors.terms ? classes.show : ''}`} style={{ marginTop: '-12px', marginBottom: '12px' }}>{t('You must agree to the terms and conditions')}</div>

              <button className={`${classes.btnSubmit} ${loading ? classes.btnLoading : ''}`} onClick={validateRegister} disabled={loading}>
                {loading && <span className={classes.spinner}></span>}
                <span>{loading ? t('Creating account…') : t('Register')}</span>
              </button>



              <div className={classes.switchText}>{t("Already have an account?")} <span onClick={() => showForm('login')}>{t('Log In')}</span></div>
            </div>
          )}

          {/* LOGIN FORM */}
          {mode === 'login' && (
            <div>
              <div className={classes.formTitle}>{t('Log In')}</div>
              <div className={classes.formSub}>{t('Continue learning from where you left off.')}</div>

              <div className={classes.fg}>
                <label className={classes.fl}>Email</label>
                <input className={`${classes.fi} ${errors.email ? classes.err : ''}`} type="email" placeholder="example@email.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                <div className={`${classes.fiErr} ${errors.email ? classes.show : ''}`}>{t('Enter a valid email address')}</div>
              </div>

              <div className={classes.fg}>
                <label className={classes.fl}>{t('Password')}</label>
                <div className={classes.pwWrap}>
                  <input className={`${classes.fi} ${errors.password ? classes.err : ''}`} type={showPassword ? 'text' : 'password'} placeholder={t("Your password")} value={password} onChange={(e) => setPassword(e.target.value)} />
                  <button className={classes.pwToggle} type="button" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeClosed /> : <EyeOpen />}
                  </button>
                </div>
                <div className={`${classes.fiErr} ${errors.password ? classes.show : ''}`}>{t('Enter your password')}</div>
              </div>

              <span className={classes.forgot} onClick={() => showForm('forgot')}>{t("Forgot your password?")}</span>
              <button className={`${classes.btnSubmit} ${loading ? classes.btnLoading : ''}`} onClick={validateLogin} disabled={loading}>
                {loading && <span className={classes.spinner}></span>}
                <span>{loading ? t('Logging in…') : t('Log In')}</span>
              </button>
              <div className={classes.switchText}>{t("Don't have an account?")} <span onClick={() => showForm('register')}>{t('Register')}</span></div>
            </div>
          )}

          {/* FORGOT PASSWORD (inline) */}
          {mode === 'forgot' && (
            <div>
              {!forgotSent ? (
                <div style={{ textAlign: 'center' }}>
                  <div className={classes.fpIconWrap}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                  </div>
                  <div className={classes.formTitle} style={{ textAlign: 'center' }}>Відновлення пароля</div>
                  <div className={classes.formSub} style={{ textAlign: 'center' }}>Введіть email, і ми надішлемо інструкції для відновлення пароля.</div>

                  <div className={classes.fg} style={{ textAlign: 'left' }}>
                    <label className={classes.fl}>Email</label>
                    <input className={`${classes.fi} ${errors.forgotEmail ? classes.err : ''}`} type="email" placeholder="example@email.com" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} />
                    <div className={`${classes.fiErr} ${errors.forgotEmail ? classes.show : ''}`}>{t('Enter a valid email address')}</div>
                    <div className={classes.fpHelper}>Ми надішлемо лист, якщо акаунт з таким email існує.</div>
                  </div>

                  <button className={`${classes.btnSubmit} ${loading ? classes.btnLoading : ''}`} onClick={submitForgot} disabled={loading}>
                    {loading && <span className={classes.spinner}></span>}
                    <span>{loading ? 'Надсилаємо…' : 'Надіслати інструкції'}</span>
                  </button>
                  <div style={{ textAlign: 'center', marginTop: '8px' }}>
                    <span className={classes.fpBack} onClick={() => showForm('login')}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg>
                      Повернутися до входу
                    </span>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center' }}>
                  <div className={`${classes.fpIconWrap} ${classes.fpSuccess}`}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
                  </div>
                  <div className={classes.formTitle} style={{ textAlign: 'center' }}>Перевірте пошту</div>
                  <div className={classes.formSub} style={{ textAlign: 'center' }}>Якщо акаунт з таким email існує, ми надіслали інструкції для відновлення пароля.</div>
                  <div className={classes.fpEmailShow}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                    <span>{forgotEmail}</span>
                  </div>
                  <button className={classes.btnSubmit} onClick={() => showForm('login')}>Повернутися до входу</button>
                  <div className={classes.fpSpamHint}>Не бачите листа? Перевірте папку «Спам» або спробуйте ще раз через кілька хвилин.</div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* VALUE PANEL */}
        <div className={classes.valuePanel}>
          <div className={classes.vpTitle}>{t("What you get after registration")}</div>
          <div className={classes.vpItem}>
            <div className={`${classes.vpIcon} ${classes.vpi1}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg></div>
            <div><h4>{t("Personal Plan")}</h4><p>{t("A course tailored to your goal and level.")}</p></div>
          </div>
          <div className={classes.vpItem}>
            <div className={`${classes.vpIcon} ${classes.vpi2}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg></div>
            <div><h4>{t("Real-life lessons")}</h4><p>{t("Each lesson: Video → Words → Quiz → AI Speaking.")}</p></div>
          </div>
          <div className={classes.vpItem}>
            <div className={`${classes.vpIcon} ${classes.vpi3}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></svg></div>
            <div><h4>{t("AI Speaking")}</h4><p>{t("Practice dialogues with AI based on videos.")}</p></div>
          </div>
          <div className={classes.vpItem}>
            <div className={`${classes.vpIcon} ${classes.vpi4}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/></svg></div>
            <div><h4>{t("Personal dictionary")}</h4><p>{t("Save words from dialogues and review them.")}</p></div>
          </div>
          <div className={classes.vpItem} style={{ marginBottom: 0 }}>
            <div className={`${classes.vpIcon} ${classes.vpi5}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg></div>
            <div><h4>{t("Learning progress")}</h4><p>{t("Track lessons, words, videos, and your learning streak.")}</p></div>
          </div>

          <div className={classes.vpFlow}>
            <div className={classes.vpFlowNode}><div className={`${classes.fd} ${classes.fdV}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="5 3 19 12 5 21 5 3"/></svg></div>Video</div>
            <span className={classes.vpFlowArr}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg></span>
            <div className={classes.vpFlowNode}><div className={`${classes.fd} ${classes.fdW}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 6v13M12 6C10.8 5.5 9.2 5 7.5 5S4.2 5.5 3 6v13c1.2-.5 2.8-1 4.5-1s3.3.5 4.5 1m0-13c1.2-.5 2.8-1 4.5-1s3.3.5 4.5 1v13c-1.2-.5-2.8-1-4.5-1s-3.3.5-4.5 1"/></svg></div>Words</div>
            <span className={classes.vpFlowArr}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg></span>
            <div className={classes.vpFlowNode}><div className={`${classes.fd} ${classes.fdQ}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9 9a3 3 0 0 1 6 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg></div>Quiz</div>
            <span className={classes.vpFlowArr}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg></span>
            <div className={classes.vpFlowNode}><div className={`${classes.fd} ${classes.fdS}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/></svg></div>Speaking</div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Auth;
